import React, { useState, useEffect, useRef, useCallback } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { authFetch, API, removeToken } from "../utils/api";
import { toast } from "sonner";
import { NotificationsPanel } from "./Layout.NotifPanel";
import { DesktopSidebar } from "./Layout.DesktopSidebar";
import { MobileBottomNav } from "./Layout.MobileBottomNav";
import { MobileMenuSheet } from "./Layout.MobileSheet";

export default function Layout() {
  const navigate = useNavigate();

  // Notifications
  const [notifCount, setNotifCount] = useState(0);
  const [notifList, setNotifList] = useState([]);
  const [notifTab, setNotifTab] = useState("nueva");
  const [showNotif, setShowNotif] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [archivingAll, setArchivingAll] = useState(false);
  const notifPanelRef = useRef(null);
  const notifBtnRef = useRef(null);

  // Profile
  const [profile, setProfile] = useState(null);

  const fetchCount = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/notifications/count`);
      if (res?.ok) { const d = await res.json(); setNotifCount(d.count || 0); }
    } catch {}
  }, []);

  const fetchNotifs = useCallback(async (tab) => {
    try {
      const res = await authFetch(`${API}/notifications?filter=${tab}`);
      if (res?.ok) setNotifList(await res.json());
    } catch {}
  }, []);

  const fetchProfile = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/profile`);
      if (res?.ok) setProfile(await res.json());
    } catch {}
  }, []);

  useEffect(() => {
    fetchCount();
    fetchProfile();
    const t = setInterval(fetchCount, 60 * 1000);
    return () => clearInterval(t);
  }, [fetchCount, fetchProfile]);

  // Presence ping — update last_seen every 30s
  useEffect(() => {
    const ping = () => authFetch(`${API}/auth/ping`, { method: "POST" }).catch(() => {});
    ping();
    const id = setInterval(ping, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (showNotif) fetchNotifs(notifTab);
  }, [showNotif, notifTab, fetchNotifs]);

  // Close notif panel on outside click
  useEffect(() => {
    const h = (e) => {
      if (
        notifPanelRef.current && !notifPanelRef.current.contains(e.target) &&
        notifBtnRef.current && !notifBtnRef.current.contains(e.target)
      ) setShowNotif(false);
    };
    if (showNotif) document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [showNotif]);

  const toggleNotif = () => setShowNotif(s => !s);

  const archiveOne = async (id) => {
    try {
      await authFetch(`${API}/notifications/${id}/archive`, { method: "PUT" });
      fetchNotifs(notifTab);
      fetchCount();
    } catch {}
  };

  const archiveAll = async () => {
    setArchivingAll(true);
    try {
      await authFetch(`${API}/notifications/archive-all`, { method: "POST" });
      fetchNotifs(notifTab);
      fetchCount();
      toast.success("Todas las notificaciones archivadas");
    } catch { toast.error("Error"); }
    finally { setArchivingAll(false); }
  };

  const handleLogout = () => {
    removeToken();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex" style={{ background: "var(--ds-surface)", fontFamily: "'Inter', sans-serif" }}>
      <DesktopSidebar
        profile={profile}
        notifCount={notifCount}
        onToggleNotif={toggleNotif}
        onLogout={handleLogout}
        notifBtnRef={notifBtnRef}
      />

      {showNotif && (
        <NotificationsPanel
          panelRef={notifPanelRef}
          notifList={notifList}
          notifTab={notifTab}
          archivingAll={archivingAll}
          onTabChange={setNotifTab}
          onArchiveOne={archiveOne}
          onArchiveAll={archiveAll}
          onClose={() => setShowNotif(false)}
        />
      )}

      <MobileBottomNav
        profile={profile}
        notifCount={notifCount}
        showNotif={showNotif}
        showMobileMenu={showMobileMenu}
        onToggleNotif={toggleNotif}
        onOpenMobileMenu={() => setShowMobileMenu(true)}
        notifBtnRef={notifBtnRef}
      />

      {showMobileMenu && (
        <MobileMenuSheet
          profile={profile}
          onClose={() => setShowMobileMenu(false)}
          onLogout={handleLogout}
        />
      )}

      <main className="flex-1 md:ml-[72px] pb-[64px] md:pb-0 overflow-auto" style={{ minHeight: "100svh" }}>
        <Outlet />
      </main>
    </div>
  );
}
